import { IntlShape } from "gatsby-plugin-react-intl";
import { object, ref, string } from "yup";

import { cleanupObject } from "../../utils/formik/objectUtils";
import { Requirement } from "../requirementList/requirementList";
import SspRegistrationData from "../ssp/sspRegistrationData";

export default class SspDataHelper {
  static preparePayloadForSubmitUser<T>(data: SspRegistrationData): T {
    let payload;

    switch (process.env.GATSBY_SITE) {
      case "de":
        payload = SspDataHelper.#getPostDataSubmitUserDe(data);
        break;
      default:
        payload = {};
    }

    return cleanupObject(payload);
  }

  static preparePayloadForSetIdentity<T>(data: SspRegistrationData, code: string): T {
    let payload;

    switch (process.env.GATSBY_SITE) {
      case "de":
        payload = SspDataHelper.#getPostDataSetIdentityDe(data, code);
        break;
      default:
        payload = {};
    }

    return cleanupObject(payload);
  }

  static getPasswordRequirements = (intl: IntlShape): Requirement[] => [
    {
      id: "1",
      regex: /.{8,}/,
      text: intl.formatMessage({ id: "registration.password.requirement.eight_characters" }),
    },
    {
      id: "4",
      regex: /[0-9]+/,
      text: intl.formatMessage({ id: "registration.password.requirement.one_number" }),
    },
    {
      id: "2",
      regex: /[A-ZÀ-ÖØ-ß]+/,
      text: intl.formatMessage({ id: "registration.password.requirement.one_uppercase" }),
    },
    {
      id: "5",
      regex: /[a-zà-öø-ÿ]+/,
      text: intl.formatMessage({ id: "registration.password.requirement.one_lowercase" }),
    },
    {
      id: "3",
      regex: /^[a-zà-öø-ÿA-ZÀ-ÖØ-ß0-9!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]*[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]+[a-zà-öø-ÿA-ZÀ-ÖØ-ß0-9!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]*$/,
      text: intl.formatMessage({ id: "registration.password.requirement.one_special_symbol" }),
    },
  ];

  static getPasswordValidationSchema = (intl: IntlShape, requirements: Requirement[]) => {
    const requiredMessage = intl.formatMessage({ id: "form.required" });

    return object().shape({
      confirmPassword: string()
        .required(requiredMessage)
        .oneOf([ref("password")], intl.formatMessage({ id: "registration.password_not_equal" }))
        .max(100, intl.formatMessage(
          { id: "form.error.max_length_exact" },
          {
            value: 100,
          },
        )),
      password: string()
        .required(requiredMessage)
        .test("requirements", intl.formatMessage({ id: "registration.password_validation" }), (value) => (
          typeof requirements.find((requirement) => (
            !RegExp(requirement.regex).test(value)
          )) === "undefined"
        )).max(100, intl.formatMessage(
          { id: "form.error.max_length_exact" },
          {
            value: 100,
          },
        )),
    });
  };

  static getCodeVerificationValidationSchema = (intl: IntlShape) => {
    const requiredMessage = intl.formatMessage({ id: "form.required" });
    const enterSixDigits = intl.formatMessage({ id: "registration.code_verification.enter_six_digit" });

    return object().shape({
      code: string()
        .min(6, enterSixDigits)
        .matches(/^[0-9]{6}/, enterSixDigits)
        .required(requiredMessage),
    });
  };

  static #getPostDataSubmitUserDe = (data: SspRegistrationData) => ({
    contact_data: {
      email: data.signUpEmailAddress,
    },
    registration_data: {
      birthdate: data.signUpBirthdate,
      firstname: data.signUpFirstname,
      lastname: data.signUpLastname,
      password: data.password,
    },
  });

  static #getPostDataSetIdentityDe = (data: SspRegistrationData, code: string) => ({
    registration_data: {
      code,
      identifier: data.identifier,
    },
  });
}
